import { Helmet } from "react-helmet";
import React from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  Stack,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import RateReviewIcon from "@mui/icons-material/RateReview";
import SearchIcon from "@mui/icons-material/Search";
import ShieldIcon from "@mui/icons-material/Shield";
import { useNavigate } from "react-router-dom";
import {
  blobDriftA,
  blobDriftB,
  contentReveal,
  kenBurnsSoft,
  meshGlow,
} from "../motion";

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  return (
    <>
      <Helmet>
        <title>Rate My Cribs | Find & Review Rental Properties</title>
        <meta
          name="description"
          content="Rate My Cribs helps renters discover, submit, and read honest reviews of apartments and rental properties. Find the best places to live, share your experiences, and make informed decisions with real tenant feedback."
        />
        <meta
          name="keywords"
          content="apartment reviews, rental property reviews, tenant feedback, rate my cribs, best apartments, honest reviews, find rentals, submit review, landlord ratings"
        />
        <link rel="canonical" href="https://ratemycribs.com/" />
        <meta
          property="og:title"
          content="Rate My Cribs | Find & Review Rental Properties"
        />
        <meta
          property="og:description"
          content="Rate My Cribs helps renters discover, submit, and read honest reviews of apartments and rental properties. Find the best places to live, share your experiences, and make informed decisions with real tenant feedback."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ratemycribs.com/" />
        <meta
          property="og:image"
          content="https://ratemycribs.com/og-image.png"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Rate My Cribs | Find & Review Rental Properties"
        />
        <meta
          name="twitter:description"
          content="Rate My Cribs helps renters discover, submit, and read honest reviews of apartments and rental properties. Find the best places to live, share your experiences, and make informed decisions with real tenant feedback."
        />
        <meta
          name="twitter:image"
          content="https://ratemycribs.com/og-image.png"
        />
      </Helmet>
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{
          flex: 1,
          width: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: { xs: 4, md: 6 },
          px: 2,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative layers — hidden from assistive tech */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background: `radial-gradient(900px 420px at 12% -10%, ${alpha(
              theme.palette.primary.main,
              0.18
            )}, transparent 55%),
            radial-gradient(700px 380px at 92% 8%, ${alpha(
              theme.palette.secondary.main,
              0.16
            )}, transparent 50%),
            linear-gradient(180deg, transparent 60%, ${alpha(
              theme.palette.primary.light,
              0.08
            )} 100%)`,
            animation: `${meshGlow} 10s ease-in-out infinite`,
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
              background: alpha(theme.palette.primary.main, 0.06),
            },
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: { xs: "8%", md: "12%" },
            right: { xs: "-12%", md: "6%" },
            width: { xs: 180, md: 280 },
            height: { xs: 180, md: 280 },
            borderRadius: "32%",
            background: alpha(theme.palette.secondary.light, 0.25),
            filter: "blur(2px)",
            transform: "rotate(12deg)",
            animation: `${blobDriftA} 19s ease-in-out infinite`,
            "@media (prefers-reduced-motion: reduce)": { display: "none" },
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            bottom: { xs: "4%", md: "10%" },
            left: { xs: "-8%", md: "4%" },
            width: { xs: 200, md: 320 },
            height: { xs: 200, md: 320 },
            borderRadius: "40%",
            background: alpha(theme.palette.primary.light, 0.22),
            filter: "blur(1px)",
            animation: `${blobDriftB} 24s ease-in-out infinite`,
            "@media (prefers-reduced-motion: reduce)": { display: "none" },
          }}
        />

        <Container
          maxWidth="md"
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              borderRadius: 4,
              p: { xs: 2.5, sm: 4, md: 5 },
              textAlign: "center",
              background: alpha(theme.palette.background.paper, 0.72),
              backdropFilter: "saturate(140%) blur(14px)",
              WebkitBackdropFilter: "saturate(140%) blur(14px)",
              border: "1px solid",
              borderColor: alpha(theme.palette.divider, 0.55),
              boxShadow: `0 24px 80px ${alpha(
                theme.palette.primary.dark,
                0.12
              )}, 0 2px 0 ${alpha(theme.palette.common.white, 0.65)} inset`,
              animation: "slideUp 0.65s ease-out both",
              "@media (prefers-reduced-motion: reduce)": {
                animation: "none",
              },
              "@media (prefers-reduced-motion: no-preference)": {
                transition: "box-shadow 0.35s ease, transform 0.35s ease",
                "&:hover": {
                  boxShadow: `0 28px 96px ${alpha(
                    theme.palette.primary.dark,
                    0.16
                  )}, 0 2px 0 ${alpha(theme.palette.common.white, 0.7)} inset`,
                  transform: "translateY(-2px)",
                },
              },
            }}
          >
            <Box
              component="figure"
              sx={{
                m: 0,
                mx: "auto",
                mb: 3,
                width: "100%",
                maxWidth: "100%",
                height: { xs: 200, sm: 260, md: 300 },
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: `0 16px 40px ${alpha("#0f172a", 0.12)}`,
                position: "relative",
                "@media (prefers-reduced-motion: no-preference)": {
                  transition: "box-shadow 0.4s ease",
                  "&:hover": {
                    boxShadow: `0 20px 48px ${alpha("#0f172a", 0.18)}`,
                  },
                },
              }}
            >
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1426122402199-be02db90eb90?q=80&w=1200&auto=format&fit=crop"
                alt="Bright living room with large windows and natural light in a rental home"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                  display: "block",
                  transformOrigin: "center center",
                  animation: `${kenBurnsSoft} 36s ease-in-out infinite alternate`,
                  "@media (prefers-reduced-motion: reduce)": {
                    animation: "none",
                  },
                }}
              />
              <Box
                aria-hidden
                sx={{
                  position: "absolute",
                  inset: 0,
                  background: `linear-gradient(0deg, ${alpha(
                    "#0f172a",
                    0.45
                  )} 0%, transparent 45%)`,
                }}
              />
              <Typography
                component="figcaption"
                variant="caption"
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 12,
                  px: 2,
                  textAlign: "center",
                  color: "common.white",
                  fontWeight: 600,
                  textShadow: "0 1px 8px rgba(0,0,0,0.35)",
                }}
              >
                Real homes. Real renter voices.
              </Typography>
            </Box>

            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 800,
                color: "text.primary",
                textAlign: "center",
                fontSize: { xs: "1.85rem", sm: "2.25rem", md: "2.75rem" },
                animation: `${contentReveal} 0.6s ease backwards`,
                animationDelay: "0.12s",
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none",
                  animationDelay: 0,
                },
              }}
            >
              Welcome to Rate My Cribs
            </Typography>
            <Typography
              variant="h6"
              component="p"
              sx={{
                color: "text.secondary",
                mb: 3,
                fontWeight: 500,
                lineHeight: 1.65,
                maxWidth: 560,
                mx: "auto",
                textAlign: "center",
                animation: `${contentReveal} 0.6s ease backwards`,
                animationDelay: "0.22s",
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none",
                  animationDelay: 0,
                },
              }}
            >
              Discover and share honest reviews of rental properties. Empowering
              renters, one review at a time.
            </Typography>

            <Stack
              direction="row"
              flexWrap="wrap"
              gap={1}
              justifyContent="center"
              sx={{
                mb: 3,
                animation: `${contentReveal} 0.6s ease backwards`,
                animationDelay: "0.32s",
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none",
                  animationDelay: 0,
                },
              }}
              role="list"
              aria-label="What you can do on Rate My Cribs"
            >
              <Chip
                role="listitem"
                icon={<RateReviewIcon aria-hidden />}
                label="Submit reviews"
                variant="outlined"
                sx={{
                  borderColor: alpha(theme.palette.primary.main, 0.35),
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  "@media (prefers-reduced-motion: no-preference)": {
                    transition:
                      "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 8px 20px ${alpha(
                        theme.palette.primary.main,
                        0.15
                      )}`,
                    },
                  },
                }}
              />
              <Chip
                role="listitem"
                icon={<SearchIcon aria-hidden />}
                label="Search addresses"
                variant="outlined"
                sx={{
                  borderColor: alpha(theme.palette.secondary.main, 0.4),
                  bgcolor: alpha(theme.palette.secondary.main, 0.06),
                  "@media (prefers-reduced-motion: no-preference)": {
                    transition:
                      "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 8px 20px ${alpha(
                        theme.palette.secondary.main,
                        0.15
                      )}`,
                    },
                  },
                }}
              />
              <Chip
                role="listitem"
                icon={<ShieldIcon aria-hidden />}
                label="Community-first"
                variant="outlined"
                sx={{
                  borderColor: alpha(theme.palette.text.secondary, 0.25),
                  bgcolor: alpha(theme.palette.common.black, 0.03),
                  "@media (prefers-reduced-motion: no-preference)": {
                    transition:
                      "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 8px 18px ${alpha("#0f172a", 0.08)}`,
                    },
                  },
                }}
              />
            </Stack>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
              alignItems="center"
              sx={{
                animation: `${contentReveal} 0.6s ease backwards`,
                animationDelay: "0.42s",
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none",
                  animationDelay: 0,
                },
              }}
            >
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => navigate("/submit")}
                aria-label="Submit a review for a property"
              >
                Submit a Review
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={() => navigate("/reviews")}
                aria-label="View reviews for properties"
              >
                View Reviews
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default HomePage;
